import { AxiosResponse } from "axios";
import { User } from "@/types/common";

type ApiRequestFn = <
    TResponseData = unknown,
    TRequestData = unknown,
    TRequestConfig = unknown,
>(
    pathOrConfig:
        | string
        | {
              path: string;
              data?: TRequestData;
              requestConfig?: TRequestConfig;
          },
    data?: TRequestData,
    requestConfig?: TRequestConfig,
) => Promise<AxiosResponse<TResponseData, TRequestConfig>>;

const apiRequestFn =
    (method: string): ApiRequestFn =>
    (pathOrConfig, data, requestConfig) => {
        const config =
            typeof pathOrConfig === "string"
                ? {
                      path: pathOrConfig,
                      data,
                      requestConfig,
                  }
                : pathOrConfig;

        return window.axios[method](
            [
                window.appConfig.apiBaseUrl.endsWith("/")
                    ? window.appConfig.apiBaseUrl.substring(
                          0,
                          window.appConfig.apiBaseUrl.length - 1,
                      )
                    : window.appConfig.apiBaseUrl,
                config.path.startsWith("/")
                    ? config.path.substring(1)
                    : config.path,
            ].join("/"),
            config.data,
            config.requestConfig,
        );
    };

const joinUrlParts = (...paths: string[]) =>
    [
        window.appConfig.apiBaseUrl.replace(/\/+$/, ""),
        ...paths.map((p) => p.replace(/^\/+/, "")),
    ].join("/");

export const responseHasStatus = <
    TResponseData = unknown,
    TRequestConfig = unknown,
>(
    response: unknown,
    status: number | number[],
): response is AxiosResponse<TResponseData, TRequestConfig> => {
    return (
        response != undefined &&
        typeof response === "object" &&
        "status" in response &&
        typeof response.status === "number" &&
        (typeof status === "number"
            ? status === response.status
            : status.includes(response.status))
    );
};

export const api = {
    delete: apiRequestFn("delete"),
    get: apiRequestFn("get"),
    post: apiRequestFn("post"),
    put: apiRequestFn("put"),

    joinUrlParts,
};

export const setUser = function (user: User): void {
    window.appConfig.user = user;
};

export const getUser = async function (): Promise<User | null> {
    return (
        window.appConfig.user ||
        (window.appConfig.user = await api
            .get("user")
            .then(({ data }) => data as User | null))
    );
};

export const getProfilePicture = async function (): Promise<string | null> {
    return getUser().then((user) => user?.profile_picture_url);
};

export const getUserId: () => Promise<string | undefined | null> = () =>
    getUser().then((user) => user?.id);

export const getUserPermissions: () => Promise<string[]> = () =>
    getUser().then((user) => user?.permissions);
